import {
	getOrderListDeliveryStatusLabel,
	getOrderListPaymentStatusLabel,
	normalizeOrderListStatusFilterValues,
} from './orderListStatusFilterOptions';
import { getOrderListStatusDrilldownLabel, ORDER_LIST_STATUS_DRILLDOWN } from './orderListStatusDrilldown';
import { getAllProvinces, getCityNameByCode } from '../cityUtils';

const trim = (value) => (typeof value === 'string' ? value.trim() : '');

const normalizeStringArray = (value) => {
	if (Array.isArray(value)) {
		return [...new Set(value.map((item) => trim(item)).filter(Boolean))];
	}
	const single = trim(value);
	return single ? [single] : [];
};

const normalizeDateRangePair = (from, to) => {
	const start = trim(from);
	const end = trim(to);
	if (start && end && start > end) {
		return { from: end, to: start };
	}
	return { from: start, to: end };
};

const formatMultiValueLabel = (values) => values.join('、');

const getProvinceNameByCode = (code) => {
	const hit = getAllProvinces().find((province) => province.code === code);
	return hit?.name || code;
};

export const createEmptyOrderListFilters = (keyword = '') => ({
	keyword: trim(keyword),
	orderId: '',
	isCompleted: '',
	paymentStatus: [],
	deliveryStatus: [],
	customerProvince: [],
	customerCity: [],
	customerName: [],
	staffContact: [],
	saleUserName: [],
	assistantUserName: [],
	orderNotes: '',
	paymentMethod: [],
	createdAtFrom: '',
	createdAtTo: '',
	completedAtFrom: '',
	completedAtTo: '',
	statusDrilldown: '',
});

export const normalizeOrderListFilters = (form) => {
	const created = normalizeDateRangePair(form.createdAtFrom, form.createdAtTo);
	const completed = normalizeDateRangePair(form.completedAtFrom, form.completedAtTo);

	const statusNormalized = normalizeOrderListStatusFilterValues(form.paymentStatus, form.deliveryStatus);
	const statusDrilldown = trim(form.statusDrilldown);
	/** 驳回单已移出订单查询列表，忽略历史钻取 */
	const normalizedDrilldown =
		statusDrilldown === ORDER_LIST_STATUS_DRILLDOWN.APPROVAL_REJECTED ? '' : statusDrilldown;

	return {
		keyword: trim(form.keyword),
		orderId: trim(form.orderId),
		isCompleted: form.isCompleted ?? '',
		paymentStatus: normalizeStringArray(statusNormalized.paymentStatus),
		deliveryStatus: normalizeStringArray(statusNormalized.deliveryStatus),
		customerProvince: normalizeStringArray(form.customerProvince),
		customerCity: normalizeStringArray(form.customerCity),
		customerName: normalizeStringArray(form.customerName),
		staffContact: normalizeStringArray(form.staffContact),
		saleUserName: normalizeStringArray(form.saleUserName),
		assistantUserName: normalizeStringArray(form.assistantUserName),
		orderNotes: trim(form.orderNotes),
		paymentMethod: normalizeStringArray(form.paymentMethod),
		createdAtFrom: created.from,
		createdAtTo: created.to,
		completedAtFrom: completed.from,
		completedAtTo: completed.to,
		statusDrilldown: normalizedDrilldown,
	};
};

export const areOrderListFiltersEqual = (a, b) => JSON.stringify(normalizeOrderListFilters(a)) === JSON.stringify(normalizeOrderListFilters(b));

export const buildColumnFiltersFromForm = (form) => {
	const normalized = normalizeOrderListFilters(form);
	const filters = [];

	if (normalized.orderId) {
		filters.push({ id: 'orderId', value: normalized.orderId });
	}
	if (normalized.isCompleted) {
		filters.push({ id: 'isCompleted', value: normalized.isCompleted });
	}
	if (normalized.paymentStatus.length > 0) {
		filters.push({ id: 'paymentStatus', value: normalized.paymentStatus });
	}
	if (normalized.deliveryStatus.length > 0) {
		filters.push({ id: 'deliveryStatus', value: normalized.deliveryStatus });
	}
	if (normalized.customerProvince.length > 0) {
		filters.push({ id: 'customerProvince', value: normalized.customerProvince });
	}
	if (normalized.customerCity.length > 0) {
		filters.push({ id: 'customerCity', value: normalized.customerCity });
	}
	if (normalized.customerName.length > 0) {
		filters.push({ id: 'customerInfo.customerName', value: normalized.customerName });
	}
	if (normalized.staffContact.length > 0) {
		filters.push({ id: 'customerInfo.staff_id', value: normalized.staffContact });
	}
	if (normalized.saleUserName.length > 0) {
		filters.push({ id: 'saleUserInfo.name', value: normalized.saleUserName });
	}
	if (normalized.assistantUserName.length > 0) {
		filters.push({ id: 'assistantUserInfo.name', value: normalized.assistantUserName });
	}
	if (normalized.orderNotes) {
		filters.push({ id: 'orderNotes', value: normalized.orderNotes });
	}
	if (normalized.paymentMethod.length > 0) {
		filters.push({ id: 'paymentMethod', value: normalized.paymentMethod });
	}
	if (normalized.createdAtFrom || normalized.createdAtTo) {
		filters.push({
			id: 'createdAt',
			value: [normalized.createdAtFrom || undefined, normalized.createdAtTo || undefined],
		});
	}
	if (normalized.completedAtFrom || normalized.completedAtTo) {
		filters.push({
			id: 'completedAt',
			value: [normalized.completedAtFrom || undefined, normalized.completedAtTo || undefined],
		});
	}
	if (normalized.statusDrilldown) {
		filters.push({ id: 'statusDrilldown', value: normalized.statusDrilldown });
	}

	return filters;
};

const formatDateRangeLabel = (from, to) => {
	if (from && to) return from === to ? from : `${from} ~ ${to}`;
	if (from) return `${from} 起`;
	if (to) return `${to} 止`;
	return '';
};

/** 已生效筛选的标签（用于展示与单项清除） */
export const buildOrderListFilterTags = (form) => {
	const normalized = normalizeOrderListFilters(form);
	const tags = [];

	if (normalized.keyword) {
		tags.push({ id: 'keyword', label: `全局搜索：${normalized.keyword}`, patch: { keyword: '' } });
	}
	if (normalized.orderId) {
		tags.push({ id: 'orderId', label: `订单编号：${normalized.orderId}`, patch: { orderId: '' } });
	}
	if (normalized.isCompleted) {
		tags.push({ id: 'isCompleted', label: `状态：${normalized.isCompleted}`, patch: { isCompleted: '' } });
	}
	if (normalized.paymentStatus.length > 0) {
		tags.push({
			id: 'paymentStatus',
			label: `财务：${formatMultiValueLabel(normalized.paymentStatus.map(getOrderListPaymentStatusLabel))}`,
			patch: { paymentStatus: [] },
		});
	}
	if (normalized.deliveryStatus.length > 0) {
		tags.push({
			id: 'deliveryStatus',
			label: `物流：${formatMultiValueLabel(normalized.deliveryStatus.map(getOrderListDeliveryStatusLabel))}`,
			patch: { deliveryStatus: [] },
		});
	}
	if (normalized.customerProvince.length > 0) {
		tags.push({
			id: 'customerProvince',
			label: `省份：${formatMultiValueLabel(normalized.customerProvince.map(getProvinceNameByCode))}`,
			patch: { customerProvince: [] },
		});
	}
	if (normalized.customerCity.length > 0) {
		tags.push({
			id: 'customerCity',
			label: `城市：${formatMultiValueLabel(normalized.customerCity.map((code) => getCityNameByCode(code) || code))}`,
			patch: { customerCity: [] },
		});
	}
	if (normalized.customerName.length > 0) {
		tags.push({
			id: 'customerName',
			label: `客户：${formatMultiValueLabel(normalized.customerName)}`,
			patch: { customerName: [] },
		});
	}
	if (normalized.staffContact.length > 0) {
		tags.push({
			id: 'staffContact',
			label: `联系人（${normalized.staffContact.length}）`,
			patch: { staffContact: [] },
		});
	}
	if (normalized.saleUserName.length > 0) {
		tags.push({
			id: 'saleUserName',
			label: `销售员：${formatMultiValueLabel(normalized.saleUserName)}`,
			patch: { saleUserName: [] },
		});
	}
	if (normalized.assistantUserName.length > 0) {
		tags.push({
			id: 'assistantUserName',
			label: `助理：${formatMultiValueLabel(normalized.assistantUserName)}`,
			patch: { assistantUserName: [] },
		});
	}
	if (normalized.paymentMethod.length > 0) {
		tags.push({
			id: 'paymentMethod',
			label: `支付：${formatMultiValueLabel(normalized.paymentMethod)}`,
			patch: { paymentMethod: [] },
		});
	}
	if (normalized.orderNotes) {
		tags.push({ id: 'orderNotes', label: `备注：${normalized.orderNotes}`, patch: { orderNotes: '' } });
	}
	if (normalized.createdAtFrom || normalized.createdAtTo) {
		tags.push({
			id: 'createdAt',
			label: `下单：${formatDateRangeLabel(normalized.createdAtFrom, normalized.createdAtTo)}`,
			patch: { createdAtFrom: '', createdAtTo: '' },
		});
	}
	if (normalized.completedAtFrom || normalized.completedAtTo) {
		tags.push({
			id: 'completedAt',
			label: `完成：${formatDateRangeLabel(normalized.completedAtFrom, normalized.completedAtTo)}`,
			patch: { completedAtFrom: '', completedAtTo: '' },
		});
	}
	if (normalized.statusDrilldown) {
		tags.push({
			id: 'statusDrilldown',
			label: `状态钻取：${getOrderListStatusDrilldownLabel(normalized.statusDrilldown)}`,
			patch: { statusDrilldown: '' },
		});
	}

	return tags;
};

/** 已生效高级筛选标签（不含列表关键词搜索） */
export const buildOrderListAdvancedFilterTags = (form) => buildOrderListFilterTags(form).filter((tag) => tag.id !== 'keyword');

/** 比较高级筛选是否一致（忽略关键词） */
export const areOrderListAdvancedFiltersEqual = (left, right) =>
	areOrderListFiltersEqual(
		normalizeOrderListFilters({ ...left, keyword: '' }),
		normalizeOrderListFilters({ ...right, keyword: '' }),
	);

/** 除全局搜索外是否填写了高级筛选 */
export const hasAdvancedOrderListFilterValues = (form) => {
	const normalized = normalizeOrderListFilters(form);

	return Boolean(
		normalized.orderId ||
		normalized.isCompleted ||
		normalized.paymentStatus.length > 0 ||
		normalized.deliveryStatus.length > 0 ||
		normalized.customerProvince.length > 0 ||
		normalized.customerCity.length > 0 ||
		normalized.customerName.length > 0 ||
		normalized.staffContact.length > 0 ||
		normalized.saleUserName.length > 0 ||
		normalized.assistantUserName.length > 0 ||
		normalized.orderNotes ||
		normalized.paymentMethod.length > 0 ||
		normalized.createdAtFrom ||
		normalized.createdAtTo ||
		normalized.completedAtFrom ||
		normalized.completedAtTo ||
		normalized.statusDrilldown,
	);
};

/** 列头漏斗：某筛选项是否已生效 */
export const isOrderListColumnFilterActive = (form, filterKey) => {
	const normalized = normalizeOrderListFilters(form);
	const value = normalized[filterKey];
	if (Array.isArray(value)) return value.length > 0;
	return Boolean(value);
};

/** 列头漏斗：清空某筛选项的 patch */
export const getOrderListColumnFilterClearPatch = (filterKey) => {
	const empty = createEmptyOrderListFilters();
	if (!(filterKey in empty)) return {};
	return { [filterKey]: empty[filterKey] };
};
